import {
  makeStyles,
  createStyles,
  Theme,
  Dialog,
  DialogContent,
  Grid,
  Box,
  DialogTitle,
  useTheme,
  useMediaQuery
} from "@material-ui/core";
import { QuoteLineItem } from "../../../api/Quote/QuoteLineItem";
import { colors } from "swyft-react-core";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { quateAddonQuantityValue } from "../../../app/utils";
import QuoteButtonAdd from "./QuoteButtonAdd";
import QuoteAddonChangeQuantity from "./QuoteAddonChangeQuantity";
import { TF } from "../../../components/typography/TF";

const QuoteAddonInfoDialog = ({ open, addon, onAdd, onClose, onChangeQuantity, allowEdits }: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const handleAdd = async () => {
    handleClose();
    await onAdd(addon);
  };

  const handleClose = () => {
    onClose();
  };

  if (!addon) {
    return null;
  }

  const highlightsSection = (addon.highlights || addon.description) && (
    <Box mt={3}>
      <TF variant="h6" paragraph>
        Product highlights
      </TF>
      <Box textAlign="left">
        <TF variant="subtitle1" paragraph>
          {addon.description}
        </TF>
        <ul className={classes.list}>
          {addon.highlights?.map((i) => (
            <li key={"highlights-addon-info" + i}>{i}</li>
          ))}
        </ul>
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" maxWidth="md">
        <DialogTitle disableTypography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box p={2}>
            <Grid key={addon.name} container>
              <Grid item md={5} xs={12}>
                <img className={classes.image} alt={addon.name} src={addon.imageUrl} />
              </Grid>
              <Grid item md={7} xs={12}>
                <Box textAlign={isSmall ? "center" : "left"}>
                  <TF variant={isSmall ? "h6" : "h5"} bold>
                    {addon.name}
                  </TF>
                  <TF variant="h6">{addon.highLevelDescription}</TF>
                  <Box mt={3}>
                    <Grid container spacing={isSmall ? 2 : 0}>
                      <Grid item md={12}>
                        <QuoteAddonChangeQuantity
                          addon={addon}
                          onChangeQuantity={onChangeQuantity}
                          hideOnMin={true}
                          hideOnSmall={false}
                          showRecommend={true}
                          showOneLine={true}
                          disabled={!allowEdits}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <TF variant={isSmall ? "h6" : "h5"} format="price" bold>
                          {quateAddonQuantityValue(addon)}
                        </TF>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <Box className={classes.root}>
                          <QuoteButtonAdd addon={addon} onAddAddon={handleAdd} fullWidth disabled={!allowEdits} />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {highlightsSection}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuoteAddonInfoDialog;

interface Props {
  className?: string;
  open: boolean;
  addon: QuoteLineItem | null;
  onAdd: Function;
  onClose: Function;
  onChangeQuantity: (addon: QuoteLineItem, quantity: number) => void;
  allowEdits: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& [class*="Button-contained.Mui-disabled"]': {
        backgroundColor: colors.white,
        border: "1px dashed black",
        fontWeight: "bold",
        color: colors.darkGrey
      }
    },
    image: {
      margin: "0 auto",
      display: "block",
      width: "100%",
      maxWidth: "15rem"
    },
    colorGray: {
      color: colors.grey
    },
    icon: {
      verticalAlign: "text-top"
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    list: {
      "paddingLeft": "20px",
      "& li": {
        marginTop: theme.spacing(1)
      },
      "& li::marker": {
        color: colors.secondary
      }
    }
  })
);
